import axiosInstance from "../utils/axios";

const API_ENDPOINTS = {
  PAYMENT: "api/Payment",
};

export const getPaymentGateways = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.PAYMENT}/payment-gateways`,
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing payment gateways data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      const normalizedData = Array.isArray(parsedData)
        ? parsedData
        : parsedData
          ? [parsedData]
          : [];

      return {
        success: true,
        data: normalizedData,
        message: responseData.message || "Payment gateways fetched successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to fetch payment gateways");
  } catch (error) {
    console.error("Error fetching payment gateways:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch payment gateways",
      data: [],
    };
  }
};

export const createCashfreePaymentLink = async (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      success: false,
      error: "A valid payload is required to create a Cashfree payment link",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/create-cashfree`,
      payload,
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing payment link data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Payment link created successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to create Cashfree payment link");
  } catch (error) {
    console.error("Error creating Cashfree payment link:", error);

    return {
      success: false,
      error: error.message || "Failed to create Cashfree payment link",
      data: null,
    };
  }
};


export const createManualPayment = async (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      success: false,
      error: "A valid payload is required to record manual payment",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/create-manual-payment`,
      payload
    );

    const responseData = response?.data || {};

    // SUCCESS CONDITION
    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing manual payment data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Manual payment recorded successfully",
        rawResponse: responseData,
      };
    }

    // If statusCode != 200 -> Throw error
    throw new Error(responseData.message || "Failed to record manual payment");
  } catch (error) {
    console.error("Error creating manual payment:", error);

    return {
      success: false,
      error: error.message || "Failed to record manual payment",
      data: null,
    };
  }
};


export const verifyCashfreePaymentLink = async (linkId) => {
  if (!linkId) {
    return {
      success: false,
      error: "LinkId is required to verify a Cashfree payment link",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.PAYMENT}/verify/${linkId}`,
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              const data = JSON.parse(responseData.data);

              if (
                data &&
                typeof data === "object" &&
                typeof data.ResponseJson === "string"
              ) {
                try {
                  data.ResponseJson = JSON.parse(data.ResponseJson);
                } catch (nestedParseError) {
                  console.error(
                    "Error parsing payment verification ResponseJson:",
                    nestedParseError,
                  );
                }
              }

              return data;
            } catch (parseError) {
              console.error("Error parsing payment verification data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Payment link verified successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to verify Cashfree payment link");
  } catch (error) {
    console.error("Error verifying Cashfree payment link:", error);

    return {
      success: false,
      error: error.message || "Failed to verify Cashfree payment link",
      data: null,
    };
  }
};

export const getLeadPaymentLinks = async (leadId, integrationProductId = null) => {
  if (!leadId) {
    return {
      success: false,
      error: "LeadId is required to fetch payment links",
      data: [],
    };
  }

  try {
    const queryString = integrationProductId ? `?IntegrationProductId=${integrationProductId}` : "";

    const response = await axiosInstance.get(
      `${API_ENDPOINTS.PAYMENT}/lead/${leadId}/payment-links${queryString}`
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing lead payment links data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: Array.isArray(parsedData) ? parsedData : parsedData ?? [],
        message: responseData.message || "Payment links fetched successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to fetch payment links for lead");
  } catch (error) {
    console.error("Error fetching payment links for lead:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch payment links for lead",
      data: [],
    };
  }
};

export const cancelCashfreePaymentLink = async (linkId) => {
  if (!linkId) {
    return {
      success: false,
      error: "LinkId is required to cancel a Cashfree payment link",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/cancel-cashfree-link/${linkId}`,
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing cancelled payment link data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Payment link cancelled successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to cancel Cashfree payment link");
  } catch (error) {
    console.error("Error cancelling Cashfree payment link:", error);

    return {
      success: false,
      error: error.message || "Failed to cancel Cashfree payment link",
      data: null,
    };
  }
};

////////// Service function related to payment catalogs //////////

export const getPaymentCatalog = async () => {
  try {
    const response = await axiosInstance.get("/api/Integration/GetPaymentCatalog");

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === "Success") {
      let parsedData = [];

      try {
        // Parse the stringified JSON data (since your API returns `data` as a string)
        parsedData = JSON.parse(resData.data || "[]");
      } catch (err) {
        console.error("Error parsing payment catalog data:", err);
      }

      return {
        success: true,
        message: resData.message || "Payment catalog fetched successfully",
        data: parsedData,
        totalRecords: resData.totalRecords || parsedData.length || 0,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || "Failed to fetch payment catalog",
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error("Error fetching payment catalog:", error);

    return {
      success: false,
      message: "Something went wrong while fetching payment catalog",
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};

export const upsertPaymentCatalog = async (payload) => {
  try {
    const response = await axiosInstance.post(
      "/api/Integration/UpsertPaymentAmountCatalog",
      payload
    );

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === "Success") {
      return {
        success: true,
        message: resData.message || "Payment catalog saved successfully",
        data: resData.data || null,
      };
    }

    // API returned failure
    return {
      success: false,
      message: resData?.message || "Failed to save payment catalog",
      data: null,
    };
  } catch (error) {
    console.error("Error upserting payment catalog:", error);

    return {
      success: false,
      message: "Something went wrong while saving payment catalog",
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

export const deletePaymentCatalog = async (paymentAmountId) => {
  try {
    const response = await axiosInstance.delete(
      `/api/Integration/DeletePaymentAmountCatalog`,
      {
        params: { paymentAmountId }, // passed as query param
      }
    );

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === "Success") {
      return {
        success: true,
        message: resData.message || "Payment catalog deleted successfully",
        data: resData.data ? JSON.parse(resData.data) : null, // your API returns data as a JSON string
      };
    }

    // API returned failure
    return {
      success: false,
      message: resData?.message || "Failed to delete payment catalog",
      data: null,
    };
  } catch (error) {
    console.error("Error deleting payment catalog:", error);

    return {
      success: false,
      message: "Something went wrong while deleting payment catalog",
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

////////// Service function to fetch application forms //////////

export const getApplicationForms = async () => {
  try {
    const response = await axiosInstance.get("/api/FormDesk/GetApplicationForms");

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === "Success") {
      let parsedData = [];

      try {
        // Parse the stringified JSON data (since API returns `data` as a string)
        parsedData = JSON.parse(resData.data || "[]");
      } catch (err) {
        console.error("Error parsing application forms data:", err);
      }

      // Log data for debugging (optional, but good for robustness to see structure)
      // console.log("Application forms raw data:", parsedData);

      // Filter forms: accept if IsActive is true, 1, or missing/undefined (to be safe if API doesn't return it)
      const activeForms = parsedData.filter((form) => 
        form.IsActive === true || 
        form.IsActive === 1 || 
        form.IsActive === undefined || 
        form.IsActive === null
      );

      return {
        success: true,
        message: resData.message || "Application forms fetched successfully",
        data: activeForms,
        totalRecords: resData.totalRecords || activeForms.length || 0,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || "Failed to fetch application forms",
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error("Error fetching application forms:", error);

    return {
      success: false,
      message: "Something went wrong while fetching application forms",
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};

export const getPaymentTypes = async () => {
  try {
    const response = await axiosInstance.get("/api/Payment/GetPaymentTypes");

    const resData = response.data;

    if (resData?.statusCode === 200 && (resData?.status === "Success" || resData?.status === "success")) {
      let parsedData = [];

      try {
        parsedData = typeof resData.data === "string" ? JSON.parse(resData.data || "[]") : (resData.data || []);
      } catch (err) {
        console.error("Error parsing payment types data:", err);
      }

      return {
        success: true,
        message: resData.message || "Payment types fetched successfully",
        data: parsedData,
        totalRecords: resData.totalRecords || parsedData.length || 0,
      };
    }

    return {
      success: false,
      message: resData?.message || "Failed to fetch payment types",
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error("Error fetching payment types:", error);

    return {
      success: false,
      message: "Something went wrong while fetching payment types",
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};

export const getInvoiceData = async (leadId) => {
  if (!leadId) {
    return {
      success: false,
      error: "LeadId is required to fetch invoice data",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.PAYMENT}/invoice/${leadId}`
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing invoice data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Invoice data fetched successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to fetch invoice data");
  } catch (error) {
    console.error("Error fetching invoice data:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch invoice data",
      data: null,
    };
  }
};


export const recordOfflinePayment = async (payload) => {
  if (!payload || !payload.LeadId) {
    return {
      success: false,
      error: "LeadId is required to record offline payment",
      data: null,
    };
  }

  try {
    // Build FormData to support file attachment
    const formData = new FormData();
    formData.append("LeadId", payload.LeadId);
    formData.append("PaymentMethod", payload.PaymentMethod);
    formData.append("Amount", payload.Amount);

    if (payload.PaidTo) {
      formData.append("PaidTo", payload.PaidTo);
    }
    if (payload.ReceivedBy) {
      formData.append("ReceivedBy", payload.ReceivedBy);
    }
    if (payload.Notes) {
      formData.append("Notes", payload.Notes);
    }
    if (payload.PaymentDate) {
      formData.append("PaymentDate", payload.PaymentDate);
    }
    if (payload.Attachment) {
      formData.append("Attachment", payload.Attachment);
    }
    if (payload.Attachments && payload.Attachments.length > 0) {
      payload.Attachments.forEach((file) => {
        formData.append("Attachments", file);
      });
    }
    if (payload.ExistingAttachmentsJson) {
      formData.append("ExistingAttachmentsJson", payload.ExistingAttachmentsJson);
    }
    if (payload.PaymentId) {
      formData.append("PaymentId", payload.PaymentId);
    }
    if (payload.VerifyStatus !== undefined) {
      formData.append("VerifyStatus", payload.VerifyStatus);
    } else if (payload.IsRefunded !== undefined) {
      formData.append("VerifyStatus", payload.IsRefunded ? 3 : 0);
    }

    const response = await axiosInstance.post("/api/Leads/RecordOfflinePayment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const resData = response?.data || {};

    if (
      resData.statusCode === 200 &&
      (resData.status === "Success" || resData.status === "success")
    ) {
      let parsedData = null;
      try {
        parsedData = typeof resData.data === "string" ? JSON.parse(resData.data) : resData.data;
      } catch (err) {
        parsedData = resData.data;
      }

      return {
        success: true,
        message: resData.message || "Offline payment recorded successfully",
        data: parsedData,
        formId: resData.formId || resData.applicationFormId || (parsedData && parsedData.formId) || (parsedData && parsedData.applicationFormId),
        rawResponse: resData,
      };
    }

    throw new Error(resData.message || "Failed to record offline payment");
  } catch (error) {
    console.error("Error recording offline payment:", error);

    return {
      success: false,
      error: error.message || "Failed to record offline payment",
      data: null,
    };
  }
};


export const getUsersForPaymentReciever = async (search = "") => {
  try {
    const response = await axiosInstance.get(`/api/Payment/GetUsersForPaymentReciever`, {
      params: { Search: search }
    });

    const resData = response.data;

    if (resData?.statusCode === 200 && (resData?.status === "Success" || resData?.status === "success")) {
      let parsedData = [];

      try {
        parsedData = typeof resData.data === "string" ? JSON.parse(resData.data || "[]") : (resData.data || []);
      } catch (err) {
        console.error("Error parsing users for payment receiver data:", err);
      }

      return {
        success: true,
        message: resData.message || "Users fetched successfully",
        data: parsedData,
        totalRecords: resData.totalRecords || parsedData.length || 0,
      };
    }

    return {
      success: false,
      message: resData?.message || "Failed to fetch users",
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error("Error fetching users for payment receiver:", error);

    return {
      success: false,
      message: "Something went wrong while fetching users",
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};


export const getCounsellorsForPaymentReciever = async (search = "") => {
  try {
    const response = await axiosInstance.get(`/api/Payment/GetCounsellorsForPaymentReciever`, {
      params: { Search: search }
    });

    const resData = response.data;

    if (resData?.statusCode === 200 && (resData?.status === "Success" || resData?.status === "success")) {
      let parsedData = [];

      try {
        parsedData = typeof resData.data === "string" ? JSON.parse(resData.data || "[]") : (resData.data || []);
      } catch (err) {
        console.error("Error parsing counsellors for payment receiver data:", err);
      }

      return {
        success: true,
        message: resData.message || "Counsellors fetched successfully",
        data: parsedData,
        totalRecords: resData.totalRecords || parsedData.length || 0,
      };
    }

    return {
      success: false,
      message: resData?.message || "Failed to fetch counsellors",
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error("Error fetching counsellors for payment receiver:", error);

    return {
      success: false,
      message: "Something went wrong while fetching counsellors",
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Generate a payment receipt
 * @param {Object} payload { paymentLinkId, leadId, receiptType }
 * receiptType: 1 for internal, 2 for Student
 */
export const GeneratePaymentReceipt = async (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      success: false,
      error: "A valid payload is required to generate payment receipt",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/GeneratePaymentReceipt`,
      payload,
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing payment receipt data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: parsedData ?? null,
        message: responseData.message || "Receipt generated successfully",
        rawResponse: responseData,
      };
    }

    return {
      success: false,
      message: responseData?.message || "Failed to generate payment receipt",
      data: null,
    };
  } catch (error) {
    console.error("Error generating payment receipt:", error);

    return {
      success: false,
      error: error.message || "Failed to generate payment receipt",
      data: null,
    };
  }
};

export const getPaymentVerificationLeads = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/GetPaymentVerificationLeads`,
      payload
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      const parsedData =
        typeof responseData.data === "string"
          ? (() => {
            try {
              return JSON.parse(responseData.data);
            } catch (parseError) {
              console.error("Error parsing payment verification leads data:", parseError);
              return responseData.data;
            }
          })()
          : responseData.data;

      return {
        success: true,
        data: Array.isArray(parsedData) ? parsedData : parsedData ?? [],
        message: responseData.message || "Payments fetched successfully",
        rawResponse: responseData,
        totalRecords: responseData.totalRecords || 0,
        pendingCount: responseData.pendingCount || 0,
        processedCount: responseData.processedCount || 0,
        rejectedCount: responseData.rejectedCount || 0,
        refundedCount: responseData.refundedCount || 0,
        totalPendingAmount: responseData.totalPendingAmount || 0,
        totalApprovedAmount: responseData.totalApprovedAmount || 0,
        totalRejectedAmount: responseData.totalRejectedAmount || 0,
        totalRefundedAmount: responseData.totalRefundedAmount || 0,
      };
    }

    throw new Error(responseData.message || "Failed to fetch payment verification leads");
  } catch (error) {
    console.error("Error fetching payment verification leads:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch payment verification leads",
      data: [],
    };
  }
};

export const VerifyOrRejectPayment = async (payload) => {
  if (!payload || !payload.paymentId) {
    return {
      success: false,
      error: "PaymentId is required",
      data: null,
    };
  }

  const normalizedPayload = {
    paymentId: payload.paymentId,
    verifyStatus: payload.verifyStatus !== undefined ? payload.verifyStatus :
                  payload.isVerified ? 1 :
                  payload.isRejected ? 2 :
                  payload.isRefunded ? 3 : 0,
    remarks: payload.remarks
  };

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/VerifyOrRejectPayment`,
      normalizedPayload
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      return {
        success: true,
        data: responseData.data ?? null,
        message: responseData.message || "Payment status updated successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to update payment status");
  } catch (error) {
    console.error("Error updating payment status:", error);

    return {
      success: false,
      error: error.message || "Failed to update payment status",
      data: null,
    };
  }
};

export const bulkVerifyOrRejectPayment = async (payload) => {
  if (!payload || !payload.paymentIds || payload.paymentIds.length === 0) {
    return {
      success: false,
      error: "PaymentIds are required for bulk action",
    };
  }

  const normalizedPayload = {
    paymentIds: payload.paymentIds,
    verifyStatus: payload.verifyStatus !== undefined ? payload.verifyStatus :
                  payload.isVerified ? 1 :
                  payload.isRejected ? 2 :
                  payload.isRefunded ? 3 : 0,
    remarks: payload.remarks
  };

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.PAYMENT}/BulkVerifyOrRejectPayment`,
      normalizedPayload
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      return {
        success: true,
        message: responseData.message || "Bulk action completed successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to complete bulk action");
  } catch (error) {
    console.error("Error in bulk verification:", error);
    return {
      success: false,
      error: error.message || "Failed to complete bulk action",
    };
  }
};

export const deleteOfflinePayment = async (paymentLinkId) => {
  if (!paymentLinkId) {
    return {
      success: false,
      error: "PaymentLinkId is required",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `/api/Leads/DeleteOfflinePayment/${paymentLinkId}`
    );
    const responseData = response?.data || {};

    if (
      responseData.statusCode === 200 &&
      (responseData.status === "Success" || responseData.status === "success")
    ) {
      return {
        success: true,
        data: responseData.data ?? null,
        message: responseData.message || "Payment deleted successfully",
        rawResponse: responseData,
      };
    }

    throw new Error(responseData.message || "Failed to delete payment");
  } catch (error) {
    console.error("Error deleting payment:", error);

    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to delete payment",
      data: null,
    };
  }
};
